<!--
📌 Componente: VitrineView.vue
📂 Caminho: src/views/VitrineView.vue
📝 Descrição: Página "Vitrine" com busca, filtros, carrossel, grids e modais.
-->

<template>
  <div class="min-h-screen flex flex-col bg-cinza-claro">
    <VitrineHeaderNotification @height-change="onNotificationHeight" />

    <!-- Cabeçalho -->
    <VitrineHeader
      ref="headerEl"
      v-model="searchQuery"
      :is-scrolled="isScrolled"
      :suggestions="suggestions"
      :phone-number="'+4964218733319'"
      phone-label="06421 87333-19"
      :style="headerOffsetStyle"
      @focus-search="showNavigationSection('Alle')"
      @select-suggestion="onSelectSuggestion"
      @logo-click="onLogoClick"
      @contact-click="scrollToFooter"
    />

    <!-- Filtros -->
    <div
      ref="filtersEl"
      class="fixed left-0 right-0 z-30 bg-cinza-claro/95 backdrop-blur border-b border-cinza-200"
      :style="filtersOffsetStyle"
    >
      <div class="container mx-auto px-4 py-3">
        <VitrineFilters
          v-model="filters"
          v-model:expanded="filtersExpanded"
          :categories="categories"
          :brands="filteredBrands"
        />
      </div>
    </div>

    <!-- Conteúdo Principal -->
    <main class="flex-grow pb-1 md:pb-2" :style="mainPaddingStyle">
      <!-- Divisão Inicial -->
      <div v-if="currentSection === 'inicial'" class="container mx-auto px-4">
        <div class="grid grid-cols-3 gap-6 mb-12">
          <div class="col-span-1">
            <!-- Carrossel de destaque -->
            <VitrineCarousel :images="carouselImages" />
          </div>

          <div class="col-span-2">
            <!-- Bloco: Was gibt's Neues -->
            <VitrineSectionBlock
              v-if="randomizedNewsProducts"
              :title="$t('vitrine.sections.news')"
              :products="randomizedNewsProducts"
              :placeholderImage="placeholderImage"
              :campaigns-cat="rabattKategorien"
              :campaigns-prod="rabattProdukte"
              :limit="4"
              @open="openModal"
              @go-to-section="showNavigationSection"
            />
          </div>
        </div>

        <!-- Demais seções iniciais -->
        <VitrineSectionBlock
          v-for="(section, key) in randomizedSections"
          :key="key"
          :title="displayTitle(key)"
          :products="section"
          :placeholderImage="placeholderImage"
          :campaigns-cat="rabattKategorien"
          :campaigns-prod="rabattProdukte"
          :limit="4"
          @open="openModal"
          @go-to-section="showNavigationSection"
          class="mb-10"
        />
      </div>

      <!-- Divisão Navegação -->
      <div v-if="currentSection === 'navegacao'" class="container mx-auto px-4">
        <VitrineProductGrid
          :products="displayedProducts"
          :placeholderImage="placeholderImage"
          :campaigns-cat="rabattKategorien"
          :campaigns-prod="rabattProdukte"
          @open="openModal"
        />
      </div>
    </main>

    <!-- Modal de Produto -->
    <VitrineProductModal
      v-if="selectedProduct"
      :product="selectedProduct"
      :images="selectedProduct.images"
      :selectedImage="selectedModalImage"
      :characteristicDescriptions="characteristicDescriptions"
      :campaigns-cat="rabattKategorien"
      :campaigns-prod="rabattProdukte"
      @close="closeModal"
      @select-image="selectedModalImage = $event"
      @open-fullscreen="openFsFromIndex"
    />

    <!-- Fullscreen Carousel -->
    <VitrineFullscreenCarousel
      v-if="fullscreen"
      :images="selectedProduct?.images || []"
      :activeIndex="fullscreenActiveIndex"
      @close="fullscreen = false"
      @prev="prevImage"
      @next="nextImage"
    />

    <!-- Botão Ver Tudo (Alle) -->
    <div
      class="container mx-auto mt-12 mb-8 text-center"
      v-if="currentSection === 'inicial'"
    >
      <button
        v-if="currentSection === 'inicial'"
        @click="showNavigationSection('Alle')"
        class="bg-verde-suave text-branco text-xl font-bold py-3 px-12 rounded shadow-lg hover:bg-azul-profundo transition-all transform hover:scale-105 duration-300"
      >
        {{ $t("vitrine.sections.all") }}
      </button>

      <!-- Seção Outdoor -->
      <!-- Seção Outdoor -->
      <div
        class="container mx-auto mt-16 mb-8 px-4"
        v-if="currentSection === 'inicial'"
      >
        <a
          href="/outdoor?auto=true"
          target="_blank"
          @click="trackOutdoorClick"
          class="group relative block w-full overflow-hidden rounded bg-cinza-900 shadow-2xl transition-transform hover:scale-[1.01]"
        >
          <!-- Background Gradiente -->
          <div
            class="absolute inset-0 bg-gradient-to-br from-cinza-900 via-texto-principal to-cinza-900 opacity-95"
          ></div>

          <!-- Conteúdo: Grid 2 Colunas -->
          <div
            class="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center py-16 px-6 sm:px-10 lg:px-16"
          >
            <!-- Bloco 1: Textos (Esquerda no desktop, Topo no mobile) -->
            <div class="space-y-3 sm:space-y-4 text-center md:text-left">
              <h3
                class="text-4xl md:text-5xl font-bold text-branco tracking-wide leading-tight"
              >
                {{ $t("vitrine.carousel.galleryAria") }}
              </h3>
              <p
                class="text-borda-suave text-base md:text-lg font-light inline-flex items-center justify-center md:justify-start gap-2"
              >
                <span>{{ $t("vitrine.carousel.alt") }}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6 text-verde-suave"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </p>

              <!-- Indicador de ação "Nova aba" -->
            </div>

            <!-- Bloco 2: Ícone TV + Player (Direita no desktop, Baixo no mobile) -->
            <div
              class="flex items-center justify-center relative z-10 text-verde-suave transition-transform duration-500 group-hover:scale-105"
            >
              <!-- TV Monito (Base) -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-40 w-40 md:h-56 md:w-56 text-verde-suave/90"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>

              <!-- Player Icon (Overlay no centro da TV) -->
              <div
                class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center pb-3 md:pb-4 text-branco drop-shadow-2xl"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="pointer-events-none h-16 w-16 md:h-24 md:w-24 bg-azul-profundo/30 rounded-full backdrop-blur-md p-1 text-branco will-change-transform"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>

    <!-- Öffnungszeiten -->
    <div class="container mx-auto mt-8">
      <VitrineOffnungszeiten />
    </div>

    <!-- Mapa / Como chegar -->
    <div class="container mx-auto mt-8 mb-16">
      <VitrineMaps />
    </div>

    <!-- Rodapé -->
    <VitrineFooter ref="footerEl" @open-legal="openLegal" />

    <!-- Modal legal fullscreen -->
    <VitrineModal v-model="showLegalModal" :type="legalType" />

    <!-- Botão Flutuante (FAB) Alle -->
    <transition name="fade">
      <button
        v-if="currentSection === 'inicial'"
        @click="showNavigationSection('Alle')"
        :class="[
          'fixed bottom-6 right-0 z-40 py-4 pl-6 pr-4 rounded-l rounded-r-none shadow-2xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center',
          isScrolling
            ? 'bg-verde-suave/20 backdrop-blur-sm hover:bg-azul-profundo hover:opacity-100'
            : 'bg-verde-suave hover:bg-azul-profundo',
        ]"
        :aria-label="$t('vitrine.sections.all')"
        :title="$t('vitrine.sections.all')"
      >
        <span class="text-branco font-bold text-lg px-2">{{
          $t("vitrine.sections.all")
        }}</span>
      </button>
    </transition>
  </div>
</template>

<script>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
  watch,
  nextTick,
  provide,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import {
  storage,
  storageRef,
  listAll,
  getDownloadURL,
  db,
  collection,
  doc,
  // Analytics para rastrear apenas a Vitrine
  analytics,
  logEvent,
} from "@/firebaseConfig";
import { onSnapshot } from "firebase/firestore";
import placeholderImage from "@/assets/images/semfoto.webp";
import VitrineHeaderNotification from "@/components/VitrineHeaderNotification.vue";
import VitrineHeader from "@/components/VitrineHeader.vue";
import VitrineFilters from "@/components/VitrineFilters.vue";
import VitrineProductGrid from "@/components/VitrineProductGrid.vue";
import VitrineSectionBlock from "@/components/VitrineSectionBlock.vue";
import VitrineProductModal from "@/components/VitrineProductModal.vue";
import VitrineFullscreenCarousel from "@/components/VitrineFullscreenCarousel.vue";
import VitrineCarousel from "@/components/VitrineCarousel.vue";
import VitrineOffnungszeiten from "@/components/VitrineOffnungszeiten.vue";
import VitrineMaps from "@/components/VitrineMaps.vue";
import VitrineModal from "@/components/VitrineModal.vue";
import VitrineFooter from "@/components/VitrineFooter.vue";

export default {
  name: "VitrineView",
  components: {
    VitrineHeaderNotification,
    VitrineHeader,
    VitrineFilters,
    VitrineProductGrid,
    VitrineSectionBlock,
    VitrineProductModal,
    VitrineFullscreenCarousel,
    VitrineCarousel,
    VitrineOffnungszeiten,
    VitrineMaps,
    VitrineModal,
    VitrineFooter,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();

    // 📌 Metadados da organização para JSON-LD
    const ORG = {
      name: "Praxis Secondhand",
      url: "https://www.praxissecondhand.de/",
      tel: "+4964218733319",
      street: "Gisselberger Straße 33",
      postal: "35037",
      city: "Marburg",
      country: "DE",
      logo: "https://www.praxissecondhand.de/static/brand/logo-512.png",
    };

    // 🖼️ Builder de alt dinâmico (usado pelos filhos via provide)
    function buildAlt(p, idx = 0) {
      const name = String(p?.name || t("vitrine.product.unknown"));
      const brand = String(p?.marke?.name || "");
      const id = String(p?.produktId || "");
      const parts = [name];
      if (brand) parts.push(brand);
      if (id) parts.push(`#${id}`);
      const base = parts.join(" – ");
      return idx > 0 ? `${base} (${idx + 1})` : base;
    }
    provide("psImageAlt", buildAlt);

    // 🧠 JSON-LD: Organization (ThriftStore) e WebSite+SearchAction
    const JSONLD_IDS = { org: "ps-jsonld-org", web: "ps-jsonld-web" };

    function buildJsonLd() {
      const org = {
        "@context": "https://schema.org",
        "@type": "ThriftStore",
        name: ORG.name,
        url: ORG.url,
        telephone: ORG.tel,
        logo: ORG.logo,
        address: {
          "@type": "PostalAddress",
          streetAddress: ORG.street,
          postalCode: ORG.postal,
          addressLocality: ORG.city,
          addressCountry: ORG.country,
        },
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "10:00",
            closes: "18:00",
          },
        ],
      };
      const web = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        url: ORG.url,
        name: ORG.name,
        potentialAction: {
          "@type": "SearchAction",
          target: `${ORG.url}?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      };
      return { org, web };
    }

    function injectJsonLd() {
      removeJsonLd();
      const { org, web } = buildJsonLd();
      const s1 = document.createElement("script");
      s1.type = "application/ld+json";
      s1.id = JSONLD_IDS.org;
      s1.textContent = JSON.stringify(org);
      const s2 = document.createElement("script");
      s2.type = "application/ld+json";
      s2.id = JSONLD_IDS.web;
      s2.textContent = JSON.stringify(web);
      document.head.appendChild(s1);
      document.head.appendChild(s2);
    }

    function removeJsonLd() {
      Object.values(JSONLD_IDS).forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
    }

    // ⚡ Preload da 1ª imagem do carrossel (melhora LCP)
    const PRELOAD_ID = "ps-preload-hero";
    function setPreloadHero(url) {
      removePreloadHero();
      if (!url) return;
      const link = document.createElement("link");
      link.id = PRELOAD_ID;
      link.rel = "preload";
      link.as = "image";
      link.href = url;
      document.head.appendChild(link);
    }
    function removePreloadHero() {
      const el = document.getElementById(PRELOAD_ID);
      if (el && el.parentNode) el.parentNode.removeChild(el);
    }

    const isScrolled = ref(false);
    const isScrolling = ref(false);
    let fabScrollTimer = 0;

    function onFabScroll() {
      isScrolling.value = true;
      if (fabScrollTimer) clearTimeout(fabScrollTimer);
      fabScrollTimer = window.setTimeout(() => {
        isScrolling.value = false;
        fabScrollTimer = 0;
      }, 200);
    }

    const searchQuery = ref("");
    const suggestions = ref([]);
    /* cache simples para sugestões + debounce */
    const sugCache = new Map();
    let sugDebounce = 0;

    const filtersExpanded = ref(false);
    const filtersEl = ref(null);
    const filtersHeight = ref(0);
    const footerEl = ref(null);

    /* debounce da busca para reduzir recomputes */
    watch(searchQuery, () => {
      clearTimeout(sugDebounce);
      sugDebounce = window.setTimeout(fetchSuggestions, 200);
    });
    const categories = ref([]);
    const characteristicDescriptions = ref([]);
    const carouselImages = ref([]);
    const products = ref([]);
    const folderEmpty = ref(false);
    const rabattKategorien = ref([]);
    const rabattProdukte = ref([]);

    const selectedProduct = ref(null);
    const selectedModalImage = ref("");
    const fullscreen = ref(false);
    const fullscreenActiveIndex = ref(0);

    // Legal modal
    const showLegalModal = ref(false);
    const legalType = ref("impressum");
    const openLegal = (t) => {
      legalType.value = t === "datenschutz" ? "datenschutz" : "impressum";
      showLegalModal.value = true;
    };

    const currentSection = ref("inicial");
    const selectedSectionFilter = ref("Alle");
    const selectedCategory = ref("Alle");
    const selectedBrand = ref("Alle");
    const sortBy = ref("Name");
    const isResettingFilters = ref(false);

    // v-model para VitrineFilters
    const filters = ref({
      section: selectedSectionFilter.value,
      category: selectedCategory.value,
      brand: selectedBrand.value,
      sortBy: sortBy.value,
    });

    const notificationHeight = ref(0);

    // Altura dinâmica do header para posicionar filtros
    const headerHeight = ref(80);
    const measureHeader = () => {
      const el = document.querySelector("header");
      const h = el ? Math.ceil(el.getBoundingClientRect().height) : 80;
      headerHeight.value = h;
    };

    const measureFilters = () => {
      const el = filtersEl.value;
      const h = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
      filtersHeight.value = h;
    };

    const handleResize = () => {
      measureHeader();
      measureFilters();
    };

    const headerOffsetStyle = computed(() => {
      if (notificationHeight.value > 0) {
        // Notification cobre o notch — header cola diretamente abaixo
        return `top: ${notificationHeight.value}px`;
      }
      // Sem notification — header cobre o notch ele mesmo
      return `top: 0; padding-top: env(safe-area-inset-top, 0px)`;
    });

    // Estilo inline para o container fixo de filtros (top em px)
    // headerHeight (medido via getBoundingClientRect) já inclui o safe-area padding quando aplicável
    const filtersOffsetStyle = computed(() => {
      const top = notificationHeight.value + headerHeight.value;
      return `top: ${top}px`;
    });

    const onNotificationHeight = (val) => {
      const numeric = Number(val);
      notificationHeight.value = Number.isFinite(numeric)
        ? Math.max(0, numeric)
        : 0;
      // Remede header/filtros no próximo tick: o padding-top do header
      // muda conforme notificationHeight (safe-area condicional)
      nextTick(() => {
        measureHeader();
        measureFilters();
      });
    };

    // Sincroniza filtros vindos do componente -> estado da View
    watch(
      filters,
      (f) => {
        if (isResettingFilters.value) return;
        const differs =
          f.section !== selectedSectionFilter.value ||
          f.category !== selectedCategory.value ||
          f.brand !== selectedBrand.value ||
          f.sortBy !== sortBy.value;
        if (currentSection.value !== "navegacao" && differs) {
          showNavigationSection(f.section);
          return;
        }
        selectedSectionFilter.value = f.section;
        selectedCategory.value = f.category;
        selectedBrand.value = f.brand;
        sortBy.value = f.sortBy;
        applyFilters();
      },
      { deep: true }
    );

    watch(filtersExpanded, async () => {
      await nextTick();
      measureFilters();
    });

    // Sincroniza estado da View -> objeto filters
    watch(
      [selectedSectionFilter, selectedCategory, selectedBrand, sortBy],
      ([s, c, b, o]) => {
        if (
          filters.value.section !== s ||
          filters.value.category !== c ||
          filters.value.brand !== b ||
          filters.value.sortBy !== o
        ) {
          filters.value = { section: s, category: c, brand: b, sortBy: o };
        }
      }
    );

    const fetchCharacteristics = (vals) => {
      const arr = Array.isArray(vals) ? vals : [];
      return arr.map((v) => String(v)).filter(Boolean);
    };

    const openModal = (product) => {
      selectedProduct.value = product;
      selectedModalImage.value = product.images[0] || placeholderImage;
      characteristicDescriptions.value = product.eigenschaften?.length
        ? fetchCharacteristics(product.eigenschaften)
        : [];

      // GA4 Log: select_item
      if (analytics) {
        logEvent(analytics, "select_item", {
          item_list_id: "vitrine_main",
          items: [
            {
              item_id: product.produktId,
              item_name: product.name,
              item_category:
                product.klassifizierung?.kategorieLabel || "Unbekannt",
              item_variant:
                product.klassifizierung?.typLabel ||
                product.klassifizierung?.gruppeLabel ||
                "Normal",
              price: toNum(product.preis),
            },
          ],
        });
      }

      const q = { ...route.query, p: product.produktId };
      router.replace({ query: q });
    };

    // Watchers para logging de filtros
    watch(selectedSectionFilter, (val) => {
      if (val !== "Alle") logFilterUse("section", val);
    });
    watch(selectedCategory, (val) => {
      if (val !== "Alle") logFilterUse("category", val);
    });
    watch(selectedBrand, (val) => {
      if (val !== "Alle") logFilterUse("brand", val);
    });

    const closeModal = () => {
      selectedProduct.value = null;
      selectedModalImage.value = "";
      const q = { ...route.query };
      delete q.p;
      router.replace({ query: q });
    };

    const openFsFromIndex = (i) => {
      if (!selectedProduct.value) return;
      const imgs = selectedProduct.value.images || [];
      const max = Math.max(0, imgs.length - 1);
      fullscreenActiveIndex.value = Math.max(0, Math.min(i, max));
      selectedModalImage.value = imgs[fullscreenActiveIndex.value] || "";
      fullscreen.value = true;
    };

    const prevImage = () => {
      const imgs = selectedProduct.value?.images || [];
      if (!imgs.length) return;
      const n = (fullscreenActiveIndex.value - 1 + imgs.length) % imgs.length;
      fullscreenActiveIndex.value = n;
      selectedModalImage.value = imgs[n];
    };

    const nextImage = () => {
      const imgs = selectedProduct.value?.images || [];
      if (!imgs.length) return;
      const n = (fullscreenActiveIndex.value + 1) % imgs.length;
      fullscreenActiveIndex.value = n;
      selectedModalImage.value = imgs[n];
    };

    const fetchProductImages = async (produtoId) => {
      try {
        const folderRef = storageRef(storage, `produtos/${produtoId}`);
        const result = await listAll(folderRef);
        const urls = await Promise.all(
          result.items.map(async (item) => await getDownloadURL(item))
        );
        return urls.length > 0 ? urls : [placeholderImage];
      } catch (error) {
        console.error(
          `Erro ao carregar imagens do produto ${produtoId}:`,
          error
        );
        return [placeholderImage];
      }
    };

    const imageCache = new Map(); // produtoId -> string[]
    let unsubVitrine = null;

    const subscribeProducts = () => {
      const refDoc = doc(collection(db, "produkteVitrine"), "zuverkaufen");

      if (typeof unsubVitrine === "function") {
        try {
          unsubVitrine();
        } catch (e) {
          /* noop */
        }
      }

      unsubVitrine = onSnapshot(
        refDoc,
        async (snap) => {
          if (!snap.exists()) {
            products.value = [];
            categories.value = [];
            rabattKategorien.value = [];
            rabattProdukte.value = [];
            return;
          }

          const data = snap.data() || {};
          const list = Array.isArray(data.produkte) ? data.produkte : [];

          const mapped = await Promise.all(
            list.map(async (p) => {
              const id = p.produktId;
              let imgs = imageCache.get(id);
              if (!imgs) {
                imgs = await fetchProductImages(id);
                imageCache.set(id, imgs);
              }
              const prev =
                products.value.find((x) => x.produktId === id) || null;
              const createdAt = prev?.createdAt || new Date();
              return { ...p, images: imgs, createdAt };
            })
          );

          products.value = mapped;

          categories.value = [
            ...new Set(
              mapped.map(
                (x) => x.klassifizierung?.kategorieLabel || "Nicht angegeben"
              )
            ),
          ];

          rabattKategorien.value = Array.isArray(data.rabattKategorien)
            ? data.rabattKategorien
            : [];
          rabattProdukte.value = Array.isArray(data.rabattProdukte)
            ? data.rabattProdukte
            : [];

          /* 🔔 Banner em tempo real do Firestore
   Prioridade: banner[] → produtos (até 5) → placeholder */
          const banner = (Array.isArray(data.banner) ? data.banner : []).filter(
            (u) => typeof u === "string" && u.startsWith("http")
          );

          if (banner.length > 0) {
            folderEmpty.value = false;
            carouselImages.value = banner;
          } else {
            folderEmpty.value = true;
            if (products.value.length > 0) {
              buildCarouselFromProducts();
            } else {
              carouselImages.value = [placeholderImage];
            }
          }

          await nextTick();
          measureFilters();
        },
        (err) => {
          console.error("onSnapshot vitrine erro:", err);
        }
      );
    };

    // --- Funções de cálculo de preço e desconto ---
    const now = ref(Date.now());
    const entryTime = ref(Date.now());
    const firstChangeTime = ref(null);
    let tick = null;

    const calcFirstChangeTime = (ms) => {
      const d = new Date(ms);
      const minute = d.getMinutes();
      const hour = d.getHours();
      const baseMin = minute % 5 === 0 ? minute + 5 : minute + 6;
      let targetMin = Math.ceil(baseMin / 5) * 5;
      let targetHour = hour;
      if (targetMin >= 60) {
        targetHour += Math.floor(targetMin / 60);
        targetMin %= 60;
      }
      const base = new Date(d);
      base.setHours(targetHour, targetMin, 0, 0);
      return base.getTime();
    };

    const slotKey = computed(() => {
      const nowMs = now.value;
      const first = firstChangeTime.value;
      if (!first || nowMs < first) {
        return `initial-${entryTime.value}`;
      }
      const d = new Date(nowMs);
      const slot = Math.floor(d.getMinutes() / 5);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const h = String(d.getHours()).padStart(2, "0");
      return `${y}-${m}-${day}-${h}-${slot}`;
    });

    const toNum = (euro) => {
      if (typeof euro === "number") return euro;
      const n = Number(
        String(euro)
          .replace(/[^0-9,.]/g, "")
          .replace(",", ".")
      );
      return Number.isFinite(n) ? n : 0;
    };

    const endOfDay = (iso) => {
      const s = String(iso || "").slice(0, 10);
      const [y, m, d] = s.split("-").map((n) => parseInt(n, 10));
      const dt = new Date(y || 1970, (m || 1) - 1, d || 1);
      dt.setHours(23, 59, 59, 999);
      return dt.getTime();
    };

    const withinWindow = (startIso, endIso) => {
      const start = endOfDay(startIso) - 24 * 60 * 60 * 1000 + 1000;
      const end = endOfDay(endIso);
      return now.value >= start && now.value <= end;
    };

    const discountOf = (product) => {
      const grp = product?.klassifizierung?.gruppeId || "";
      const kat = product?.klassifizierung?.kategorieId || "";
      const pid = product?.produktId || "";

      const valid = (x) =>
        x && x.status !== "cancelada" && withinWindow(x.startDatum, x.endDatum);

      const prodList = (rabattProdukte.value || []).filter(
        (x) => x.produktId === pid && valid(x)
      );
      const catList = (rabattKategorien.value || []).filter(
        (x) => x.gruppeId === grp && x.kategorieId === kat && valid(x)
      );

      const pickMax = (arr) =>
        arr.length
          ? arr.slice().sort((a, b) => (b.prozent || 0) - (a.prozent || 0))[0]
          : null;

      const pMax = pickMax(prodList);
      const cMax = pickMax(catList);

      let chosen = null;
      if (pMax && cMax) {
        if ((cMax.prozent || 0) > (pMax.prozent || 0)) {
          chosen = { ...cMax, src: "cat" };
        } else {
          chosen = { ...pMax, src: "prod" };
        }
      } else if (pMax) {
        chosen = { ...pMax, src: "prod" };
      } else if (cMax) {
        chosen = { ...cMax, src: "cat" };
      }

      if (!chosen) return { pct: 0 };
      return { pct: Math.abs(Number(chosen.prozent || 0)) };
    };

    const finalPriceNum = (product) => {
      const pct = discountOf(product).pct;
      const base = toNum(product.preis);
      return Math.round(base * (1 - pct / 100) * 100) / 100;
    };
    // --- GA4 Logging Helpers ---
    const logSearch = (term) => {
      if (!analytics || !term || term.length < 3) return;
      logEvent(analytics, "search", { search_term: term });
    };

    const logFilterUse = (type, value) => {
      if (!analytics) return;
      logEvent(analytics, "filter_use", {
        filter_type: type,
        value: value,
      });
    };

    // Debounce para logSearch
    let searchLogDebounce = 0;
    watch(searchQuery, (newVal) => {
      clearTimeout(searchLogDebounce);
      searchLogDebounce = window.setTimeout(() => {
        logSearch(newVal);
      }, 2000); // Loga apenas após 2s de inatividade (evitar logar digitação incompleta)
    });

    // --- Fim das funções de cálculo ---

    const filteredProducts = computed(() => {
      let filtered = [...products.value];

      if (selectedSectionFilter.value !== "Alle") {
        if (selectedSectionFilter.value === "Was gibt's Neues") {
          filtered = filtered.filter((p) => {
            const days =
              (new Date() - new Date(p.createdAt)) / (1000 * 60 * 60 * 24);
            return days < 15;
          });
        } else if (selectedSectionFilter.value === "Schnäppchen") {
          filtered = filtered.filter((p) => finalPriceNum(p) <= 5);
        } else if (selectedSectionFilter.value === "Sonderangebote") {
          filtered = filtered.filter((p) => discountOf(p).pct > 0);
        }
      }

      if (selectedCategory.value !== "Alle") {
        filtered = filtered.filter(
          (p) =>
            (selectedCategory.value === "Nicht angegeben" &&
              !p.klassifizierung?.kategorieLabel) ||
            p.klassifizierung?.kategorieLabel === selectedCategory.value
        );
      }

      if (selectedBrand.value !== "Alle") {
        filtered = filtered.filter(
          (p) =>
            (selectedBrand.value === "Nicht angegeben" && !p.marke?.name) ||
            p.marke?.name === selectedBrand.value
        );
      }

      if (searchQuery.value) {
        const norm = (s) =>
          String(s || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();

        const q = norm(searchQuery.value.trim());

        filtered = filtered.filter((p) => {
          const name = norm(p.name);
          const id = norm(p.produktId);
          const brand = norm(p.marke?.name);
          const cat = norm(p.klassifizierung?.kategorieLabel);
          const model = norm(p.modell ?? p.model);
          const type =
            norm(p.klassifizierung?.typLabel) ||
            norm(p.klassifizierung?.gruppeLabel);

          return (
            name.includes(q) ||
            id.includes(q) ||
            brand.includes(q) ||
            cat.includes(q) ||
            model.includes(q) ||
            type.includes(q)
          );
        });
      }

      if (sortBy.value === "Name") {
        filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      } else if (sortBy.value === "Preis") {
        filtered.sort(
          (a, b) => parseFloat(a.preis || 0) - parseFloat(b.preis || 0)
        );
      }

      return filtered;
    });

    const nonEmptyFilteredProducts = computed(() => {
      const allSections = {
        "Was gibt's Neues": products.value.filter((p) => {
          const days =
            (new Date() - new Date(p.createdAt)) / (1000 * 60 * 60 * 24);
          return days < 15;
        }),
        Schnäppchen: products.value.filter((p) => finalPriceNum(p) <= 5),
        Sonderangebote: products.value.filter((p) => discountOf(p).pct > 0),
        Alle: products.value,
      };
      return Object.fromEntries(
        Object.entries(allSections).filter(([, s]) => s.length > 0)
      );
    });

    const filteredSections = computed(() => {
      return Object.fromEntries(
        Object.entries(nonEmptyFilteredProducts.value).filter(
          ([key]) => key !== "Was gibt's Neues"
        )
      );
    });

    const hashSeed = (value) => {
      const str = String(value || "");
      let hash = 0;
      for (let i = 0; i < str.length; i += 1) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
      }
      return Math.abs(hash);
    };

    const orderWithSeed = (list, seed) => {
      return list
        .map((item) => ({
          score: hashSeed(`${seed}-${item?.produktId || ""}`),
          item,
        }))
        .sort((a, b) => a.score - b.score)
        .map((p) => p.item);
    };

    const pickDiverse = (ordered, limit, catKeyFn) => {
      const result = [];
      const seen = new Set();
      for (const item of ordered) {
        if (result.length >= limit) break;
        const key = catKeyFn(item);
        if (!seen.has(key)) {
          seen.add(key);
          result.push(item);
        }
      }
      if (result.length < limit) {
        for (const item of ordered) {
          if (result.length >= limit) break;
          const key = catKeyFn(item);
          if (!seen.has(key)) {
            seen.add(key);
            result.push(item);
          } else if (!result.includes(item)) {
            result.push(item);
          }
        }
      }
      return result.slice(0, limit);
    };

    const prioritizeWithPhoto = (
      list,
      seed,
      limit = 4,
      excludeIds = new Set()
    ) => {
      if (!Array.isArray(list) || list.length === 0) return null;
      const available = list.filter((p) => !excludeIds.has(p?.produktId));
      const source = available.length > 0 ? available : list;
      const withPhoto = source.filter((p) => {
        return p?.images?.[0] && p.images[0] !== placeholderImage;
      });
      const withoutPhoto = source.filter((p) => !withPhoto.includes(p));
      const ordered = [
        ...orderWithSeed(withPhoto, `${seed}-photo`),
        ...orderWithSeed(withoutPhoto, `${seed}-nop`),
      ];
      const catKey = (p) =>
        p?.klassifizierung?.kategorieId ||
        p?.klassifizierung?.kategorieLabel ||
        "none";
      return pickDiverse(ordered, limit, catKey);
    };

    const randomizedInitial = computed(() => {
      const usedIds = new Set();
      const newsList = nonEmptyFilteredProducts.value["Was gibt's Neues"];
      const news = prioritizeWithPhoto(
        newsList,
        `${slotKey.value}-news`,
        4,
        usedIds
      );
      if (news) {
        news.forEach((p) => usedIds.add(p?.produktId));
      }

      const entries = Object.entries(filteredSections.value || {}).map(
        ([key, list]) => {
          const picked =
            prioritizeWithPhoto(list, `${slotKey.value}-${key}`, 4, usedIds) ||
            [];
          picked.forEach((p) => usedIds.add(p?.produktId));
          return [key, picked];
        }
      );

      return {
        news: news || null,
        sections: Object.fromEntries(entries),
      };
    });

    const randomizedNewsProducts = computed(() => randomizedInitial.value.news);
    const randomizedSections = computed(() => randomizedInitial.value.sections);

    const filteredBrands = computed(() => {
      return [
        ...new Set(
          filteredProducts.value.map((p) => p.marke?.name || "Nicht angegeben")
        ),
      ];
    });

    const displayedProducts = computed(() => filteredProducts.value);

    const fetchSuggestions = () => {
      const norm = (s) =>
        String(s || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();

      const raw = searchQuery.value || "";
      const q = norm(raw.trim());

      if (!q) {
        suggestions.value = products.value
          .map((p) => String(p.name || ""))
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b))
          .slice(0, 5);
        return;
      }

      if (sugCache.has(q)) {
        suggestions.value = sugCache.get(q);
        return;
      }

      const scored = [];
      for (const p of products.value) {
        const name = String(p.name || "");
        const brand = String(p.marke?.name || "");
        const id = String(p.produktId || "");
        const cat = String(p.klassifizierung?.kategorieLabel || "");
        const model = String(p.modell ?? p.model ?? "");
        const type =
          String(p.klassifizierung?.typLabel || "") ||
          String(p.klassifizierung?.gruppeLabel || "");

        const nName = norm(name);
        const nBrand = norm(brand);
        const nId = norm(id);
        const nCat = norm(cat);
        const nModel = norm(model);
        const nType = norm(type);

        let score = Infinity;
        if (nName.startsWith(q)) score = 0;
        else if (nName.includes(q)) score = 1;
        else if (nBrand.startsWith(q)) score = 2;
        else if (nBrand.includes(q)) score = 3;
        else if (nCat.includes(q)) score = 4;
        else if (nModel.includes(q)) score = 5;
        else if (nType.includes(q)) score = 6;
        else if (nId.includes(q)) score = 7;

        if (score !== Infinity) {
          scored.push({ s: name || id || brand || cat, score });
        }
      }

      const out = scored
        .sort((a, b) =>
          a.score === b.score
            ? String(a.s).localeCompare(String(b.s))
            : a.score - b.score
        )
        .map((x) => x.s)
        .filter(Boolean)
        .slice(0, 5);

      sugCache.set(q, out);
      suggestions.value = out;
    };

    // Normaliza a seção recebida (label traduzido ou chave interna)
    // e retorna sempre a chave interna usada pela lógica de filtros.
    function normalizeSection(section) {
      const map = {
        Alle: t("vitrine.sections.all"),
        "Was gibt's Neues": t("vitrine.sections.news"),
        Schnäppchen: t("vitrine.sections.deals"),
        Sonderangebote: t("vitrine.sections.specialOffers"),
      };
      for (const [key, label] of Object.entries(map)) {
        if (section === key || section === label) return key;
      }
      return "Alle";
    }

    const showNavigationSection = async (section) => {
      const key = normalizeSection(section);
      currentSection.value = "navegacao";
      // garante estado interno correto
      selectedSectionFilter.value = key;
      // garante v-model do VitrineFilters já sincronizado
      filters.value = {
        section: key,
        category: selectedCategory.value,
        brand: selectedBrand.value,
        sortBy: sortBy.value,
      };
      applyFilters();
      // espera o reflow das mudanças antes de rolar ao topo
      await nextTick();
      measureHeader();
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const trackOutdoorClick = () => {
      if (analytics) {
        logEvent(analytics, "select_content", {
          content_type: "outdoor_mode",
          item_id: "outdoor_entry_point",
        });
      }
    };

    // Utilitário simples para rolar ao topo com suavidade.
    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const scrollToFooter = () => {
      const el =
        footerEl.value?.$el ||
        footerEl.value?.$?.subTree?.el ||
        footerEl.value?.$?.el ||
        document.querySelector("footer");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth" });
      }
    };

    // Fecha a barra ao clicar fora (somente em layout colapsável)
    const isCollapsible = () => window.innerWidth < 1024;

    const onDocPointerDown = (e) => {
      if (!filtersExpanded.value) return;
      if (!isCollapsible()) return;
      const el = filtersEl.value;
      if (!el) return;
      if (el.contains(e.target)) return;
      filtersExpanded.value = false;
    };

    // Clique no título/Logo do TabHeader.
    const onLogoClick = () => {
      isResettingFilters.value = true;
      selectedSectionFilter.value = "Alle";
      selectedCategory.value = "Alle";
      selectedBrand.value = "Alle";
      sortBy.value = "Name";
      filters.value = {
        section: "Alle",
        category: "Alle",
        brand: "Alle",
        sortBy: "Name",
      };
      isResettingFilters.value = false;
      currentSection.value = "inicial";
      scrollToTop();
    };

    const applyFilters = () => {
      products.value = [...products.value]; // força recomputes
    };

    const mainPaddingStyle = computed(() => {
      // headerHeight medido já inclui safe-area padding quando aplicável
      const base =
        headerHeight.value + filtersHeight.value + notificationHeight.value;
      return currentSection.value === "navegacao"
        ? `padding-top: calc(${base}px + 9rem)`
        : `padding-top: calc(${base}px + 4rem)`;
    });

    function buildCarouselFromProducts() {
      const list = products.value.map((p) => p?.images?.[0]).filter(Boolean);
      if (!list.length) return;
      const pool = Array.from(new Set(list));
      pool.sort(() => 0.5 - Math.random());
      carouselImages.value = pool.slice(0, 5);
    }

    function onSelectSuggestion(s) {
      searchQuery.value = s;
      showNavigationSection("Alle");
    }

    watch(
      products,
      (newProducts) => {
        if (folderEmpty.value && newProducts.length > 0) {
          // Pasta vazia e produtos chegaram: refaz carrossel
          buildCarouselFromProducts();
        }

        // 🔗 Verifica se deve abrir modal por query parameter após carga de dados
        const id = String(route.query.p || "");
        if (id && !selectedProduct.value && newProducts.length > 0) {
          const prod = newProducts.find((x) => x.produktId === id);
          if (prod) openModal(prod);
        }
      },
      { deep: true }
    );

    // Preload da 1ª imagem quando o carrossel mudar
    watch(
      carouselImages,
      (arr) => {
        const first = Array.isArray(arr) ? arr[0] : "";
        setPreloadHero(first || "");
      },
      { immediate: false }
    );

    onMounted(async () => {
      entryTime.value = Date.now();
      firstChangeTime.value = calcFirstChangeTime(entryTime.value);
      subscribeProducts();

      // JSON-LD da organização e site
      injectJsonLd();

      await nextTick();
      handleResize();
      window.addEventListener("resize", handleResize);
      const mq = window.matchMedia("(prefers-reduced-motion: no-preference)");
      if (mq && mq.addEventListener) {
        mq.addEventListener("change", handleResize);
      }

      // Preload inicial do carrossel (se já houver imagens)
      if (carouselImages.value && carouselImages.value.length > 0) {
        setPreloadHero(carouselImages.value[0]);
      }

      const id = String(route.query.p || "");
      if (id) {
        const prod = products.value.find((x) => x.produktId === id);
        if (prod) openModal(prod);
      }

      if (analytics) {
        logEvent(analytics, "page_view", {
          page_title: "Vitrine",
          page_path: "/",
        });
      }

      window.addEventListener(
        "scroll",
        () => (isScrolled.value = window.scrollY > 50)
      );
      window.addEventListener("scroll", onFabScroll);
      document.addEventListener("pointerdown", onDocPointerDown);

      tick = setInterval(() => {
        now.value = Date.now();
      }, 1000);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("pointerdown", onDocPointerDown);
      window.removeEventListener("scroll", onFabScroll);
      if (fabScrollTimer) clearTimeout(fabScrollTimer);

      // Limpa tags injetadas
      removeJsonLd();
      removePreloadHero();

      if (typeof unsubVitrine === "function") {
        try {
          unsubVitrine();
        } catch (e) {
          /* noop */
        }
      }
      if (tick) clearInterval(tick);
    });

    // Recalcula altura do header em mudanças de layout
    watch([isScrolled, currentSection], async () => {
      await nextTick();
      measureHeader();
      measureFilters();
    });

    // Mapeia chaves internas fixas → rótulos traduzidos.
    function displayTitle(k) {
      if (k === "Was gibt's Neues") return t("vitrine.sections.news");
      if (k === "Schnäppchen") return t("vitrine.sections.deals");
      if (k === "Sonderangebote") return t("vitrine.sections.specialOffers");
      if (k === "Alle") return t("vitrine.sections.all");
      return k;
    }

    watch(
      () => route.query.p,
      (id) => {
        const val = String(id || "");
        if (!val) {
          if (selectedProduct.value) closeModal();
          return;
        }
        const prod = products.value.find((x) => x.produktId === val);
        if (prod) openModal(prod);
      },
      { immediate: true }
    );

    return {
      // estado/UI
      isScrolled,
      isScrolling,
      searchQuery,
      suggestions,
      mainPaddingStyle,
      headerOffsetStyle,
      filtersOffsetStyle,
      headerHeight,
      filtersHeight,
      filtersExpanded,
      filtersEl,
      footerEl,
      // dados/carrossel
      carouselImages,
      // produtos/filtros
      placeholderImage,
      categories,
      products,
      filteredSections,
      nonEmptyFilteredProducts,
      randomizedSections,
      randomizedNewsProducts,
      filteredBrands,
      displayedProducts,
      // campanhas
      rabattKategorien,
      rabattProdukte,
      // filtros controlados
      filters,
      currentSection,
      selectedSectionFilter,
      selectedCategory,
      selectedBrand,
      sortBy,
      // modal/fullscreen
      selectedProduct,
      selectedModalImage,
      characteristicDescriptions,
      fullscreen,
      fullscreenActiveIndex,
      // ações
      openModal,
      closeModal,
      openFsFromIndex,
      prevImage,
      nextImage,
      showNavigationSection,
      trackOutdoorClick,
      applyFilters,
      onSelectSuggestion,
      // utilidades e handlers
      scrollToTop,
      scrollToFooter,
      onLogoClick,
      displayTitle,
      showLegalModal,
      legalType,
      openLegal,
      onNotificationHeight,
    };
  },
};
</script>

<style scoped>
.fixed.inset-0 {
  background-color: theme("colors.cinza-900 / 50%");
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

.height-enter-active,
.height-leave-active {
  transition: max-height 0.3s ease;
}

.height-enter-from,
.height-leave-to {
  max-height: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 640px) {
  .grid-cols-2 {
    grid-template-columns: 1fr;
  }

  .grid-cols-3 {
    grid-template-columns: 1fr;
  }

  .grid-cols-4 {
    grid-template-columns: 1fr;
  }
}
</style>
